import { FunctionComponent } from 'react';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { Formik } from 'formik';
import type { FileUploadHandler, FileUploadForm } from './types';

import { buildFileUploadRows } from './buildFileUploadRows.helper';
import { useClientData } from '@/providers/ClientData.provider';

const { Box, Button, Table, Title, TextField, Select, TextArea, InputFile } = vp;

type TicketCreationProps = {
  handlers: FileUploadHandler;
  defaultValues: FileUploadForm;
};

type FC = FunctionComponent<TicketCreationProps>;
const FileUploadFormContainer: FC = (props: TicketCreationProps) => {
  const { handlers, defaultValues } = props;
  const {
    response: { Country },
  } = useClientData();

  const fileUploadRow = buildFileUploadRows(Country);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onUpload}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const ticketsTable: vp.TableProps = {
          name: 'add-user-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: fileUploadRow
            .filter((x) => x.visible(values))
            .map(({ label, id, preset, type, getSelectOptions, idFileName }) => ({
              Field: label,
              Input: getField(id, preset, type, getSelectOptions, idFileName),
            })),
        };

        return (
          <form
            onSubmit={(e) => {
              handleSubmit(e);
            }}
          >
            <Box mb="md" jc="fs" ai="center" gapSize="16px">
              <Title title="Bulk Contacts Upload" />
            </Box>

            <Table {...ticketsTable} />

            <Box mt="md" jc="fe">
              <Button id="CreateTicket-submit" type="submit" label="Review" />
            </Box>
          </form>
        );
        function getField(
          id: vp.FileUploadId,
          preset: keyof vp.TextFieldPresets | undefined,
          type: 'input' | 'select' | 'textarea' | 'inputfile',
          getSelectOptions: () => {
            [key: string]: string;
          },
          idFileName?: vp.FileUploadId,
        ) {
          switch (type) {
            case 'select':
              return (
                <Select
                  variant="standard"
                  options={getSelectOptions()}
                  key={id}
                  name={id}
                  value={values[id]}
                  placeholder=""
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              );
            case 'input':
              return (
                <TextField
                  preset={preset}
                  key={id}
                  name={id}
                  variant="standard"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              );
            case 'inputfile':
              return (
                <InputFile
                  id={id}
                  key={id}
                  name={id}
                  variant="standard"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onBlurFileName={handleBlur}
                  onChangeFileName={handleChange}
                  idFileName={idFileName}
                  valueFileName={idFileName ? values[idFileName] : undefined}
                  maxSize={20}
                  validExtensions={['csv']}
                  accept="text/csv"
                  error={errors[id] && touched[id] ? errors[id] : null}
                  label="Upload File"
                />
              );
            default:
              return <TextArea key={id} name={id} onBlur={handleBlur} onChange={handleChange} maxLength={5000} />;
          }
        }
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      FileBase64: Yup.string().required('Please provide a file.'),
    });
  }
};

export default FileUploadFormContainer;
